import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Target, Clock, PoundSterling, Activity, TrendingUp, TrendingDown, MousePointerClick } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { UtilisationSummary } from '@/types/utilisation';

interface KeyPerformanceIndicatorsProps {
  summary: UtilisationSummary;
  onCardClick?: (cardType: 'utilisation' | 'hours' | 'billable' | 'peak') => void;
}

// Helper function to get utilization accent class
const getUtilizationAccent = (utilization: number): string => {
  if (utilization >= 0.85) return 'accent-success';
  if (utilization >= 0.75) return 'accent-info';
  if (utilization >= 0.60) return 'accent-warning';
  return 'accent-destructive';
};

export const KeyPerformanceIndicators: React.FC<KeyPerformanceIndicatorsProps> = ({
  summary,
  onCardClick,
}) => {
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      {/* Average Utilisation */}
      <Card 
        className={cn(
          "cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-105",
          "border-l-4",
          getUtilizationAccent(summary.averageUtilisation),
          "border-l-[var(--accent-border)]"
        )}
        onClick={() => onCardClick?.('utilisation')}
      >
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <div className={cn(
              "p-1.5 rounded-md",
              getUtilizationAccent(summary.averageUtilisation),
              "bg-[var(--accent-bg-outline)]"
            )}>
              <Target className={cn(
                "h-4 w-4",
                getUtilizationAccent(summary.averageUtilisation),
                "text-[var(--accent-text-on-outline)]"
              )} />
            </div>
            Average Utilisation
            {onCardClick && <MousePointerClick className="h-3 w-3 ml-auto opacity-50" />}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-baseline gap-2">
            <div className={cn(
              "text-2xl font-bold",
              getUtilizationAccent(summary.averageUtilisation),
              "text-[var(--accent-text-on-outline)]"
            )}>
              {(summary.averageUtilisation * 100).toFixed(1)}%
            </div>
            <Badge
              variant={summary.varianceFromTarget >= 0 ? 'default' : 'destructive'}
              className="text-xs"
            >
              {summary.varianceFromTarget >= 0 ? (
                <TrendingUp className="h-3 w-3 mr-1" />
              ) : (
                <TrendingDown className="h-3 w-3 mr-1" />
              )}
              {Math.abs(summary.varianceFromTarget * 100).toFixed(1)}%
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Target: {(summary.targetUtilisation * 100).toFixed(0)}%
          </p>
        </CardContent>
      </Card>

      {/* Total Hours */}
      <Card 
        className={cn(
          "cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-105",
          "border-l-4 accent-info border-l-[var(--accent-border)]"
        )}
        onClick={() => onCardClick?.('hours')}
      >
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <div className="p-1.5 rounded-md accent-info bg-[var(--accent-bg-outline)]">
              <Clock className="h-4 w-4 accent-info text-[var(--accent-text-on-outline)]" />
            </div>
            Total Hours
            {onCardClick && <MousePointerClick className="h-3 w-3 ml-auto opacity-50" />}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold accent-info text-[var(--accent-text-on-outline)]">
            {summary.totalHours.toFixed(1)}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {summary.totalEmployees} {summary.totalEmployees === 1 ? 'employee' : 'employees'}
          </p>
        </CardContent>
      </Card>

      {/* Billable Hours */}
      <Card 
        className={cn(
          "cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-105",
          "border-l-4 accent-success border-l-[var(--accent-border)]"
        )}
        onClick={() => onCardClick?.('billable')}
      >
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <div className="p-1.5 rounded-md accent-success bg-[var(--accent-bg-outline)]">
              <PoundSterling className="h-4 w-4 accent-success text-[var(--accent-text-on-outline)]" />
            </div>
            Billable Hours
            {onCardClick && <MousePointerClick className="h-3 w-3 ml-auto opacity-50" />}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-baseline gap-2">
            <div className="text-2xl font-bold accent-success text-[var(--accent-text-on-outline)]">
              {summary.totalBillableHours.toFixed(1)}
            </div>
            <Badge variant="secondary" className="text-xs">
              {summary.billablePercentage.toFixed(0)}%
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {summary.totalNonBillableHours.toFixed(1)} non-billable
          </p>
        </CardContent>
      </Card>

      {/* Peak Activity */}
      <Card 
        className={cn(
          "cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-105",
          "border-l-4 accent-purple border-l-[var(--accent-border)]"
        )}
        onClick={() => onCardClick?.('peak')}
      >
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <div className="p-1.5 rounded-md accent-purple bg-[var(--accent-bg-outline)]">
              <Activity className="h-4 w-4 accent-purple text-[var(--accent-text-on-outline)]" />
            </div>
            Peak Activity
            {onCardClick && <MousePointerClick className="h-3 w-3 ml-auto opacity-50" />}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold accent-purple text-[var(--accent-text-on-outline)] capitalize">
            {summary.peakUtilisationDay}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Lowest: {summary.lowestUtilisationDay}
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
